import {CheckmarkLine, EditLine, FileLine} from '@dropbox/dig-icons/assets';
import {PulseUser} from 'atoms/auth';
import {canViewEditorDocumentAdmin} from 'helpers/utils';
import {t} from 'i18next';

import {DocumentStatus} from './types';

const SWITCHBOARD_DOCUMENT_STATUSES = new Set<DocumentStatus>([
  DocumentStatus.CONTEXT,
  DocumentStatus.FRAMING,
  DocumentStatus.DRAFT_TRACKER,
  DocumentStatus.REFRESH,
  DocumentStatus.RETROSPECTIVE,
  DocumentStatus.PLANNING_COMPLETE,
]);

export const canEditDocument = (
  pulseUser: PulseUser | undefined,
  ownerLdap: string | undefined
) => {
  if (!pulseUser || !ownerLdap) {
    return false;
  }
  return pulseUser.ldap === ownerLdap || canViewEditorDocumentAdmin(pulseUser);
};

export const getDocStatus = (status: string): DocumentStatus => {
  switch (status) {
    case 'in_review':
      return DocumentStatus.IN_REVIEW;
    case 'completed':
      return DocumentStatus.COMPLETED;
    case 'template':
      return DocumentStatus.TEMPLATE;
    case 'draft':
      return DocumentStatus.DRAFT;
    case 'review':
      return DocumentStatus.READY_FOR_REVIEW;
    case 'complete':
      return DocumentStatus.PUBLISHED;
    case 'archive':
      return DocumentStatus.ARCHIVED;
    case 'context':
      return DocumentStatus.CONTEXT;
    case 'framing':
      return DocumentStatus.FRAMING;
    case 'draft_tracker':
      return DocumentStatus.DRAFT_TRACKER;
    // Legacy Postgres enum label still present on many rows; keep until DB backfill to PLANNING_COMPLETE
    case 'execution':
      return DocumentStatus.EXECUTION;
    case 'planning_complete':
      return DocumentStatus.PLANNING_COMPLETE;
    case 'refresh':
      return DocumentStatus.REFRESH;
    case 'retrospective':
      return DocumentStatus.RETROSPECTIVE;
    default:
      throw new Error(`Unhandled status: ${status}`);
  }
};

export const getStatusColor = (status: DocumentStatus) => {
  if (SWITCHBOARD_DOCUMENT_STATUSES.has(status)) {
    return 'transparent';
  }

  switch (status) {
    case DocumentStatus.IN_REVIEW:
      return 'var(--dig-color__warning__surface)';
    case DocumentStatus.COMPLETED:
      return 'var(--dig-color__success__surface--state-1)';
    case DocumentStatus.TEMPLATE:
      return 'var(--dig-color__primary__surface--state-2)';
    case DocumentStatus.DRAFT:
      return 'var(--dig-color__primary__surface--state-1)';
    case DocumentStatus.READY_FOR_REVIEW:
      return 'var(--dig-color__warning__surface)';
    case DocumentStatus.PUBLISHED:
      return 'var(--dig-color__success__surface--state-1)';
    case DocumentStatus.ARCHIVED:
      return 'var(--dig-color__opacity__surface--state-1)';
    default:
      return undefined;
  }
};

export const getStatusBorder = (status: DocumentStatus) => {
  if (SWITCHBOARD_DOCUMENT_STATUSES.has(status)) {
    return '1px solid var(--dig-color__border__subtle)';
  }

  return undefined;
};

export const getStatusIconSrc = (status: DocumentStatus) => {
  if (SWITCHBOARD_DOCUMENT_STATUSES.has(status)) {
    return undefined;
  }

  switch (status) {
    case DocumentStatus.IN_REVIEW:
      return EditLine;
    case DocumentStatus.COMPLETED:
      return CheckmarkLine;
    case DocumentStatus.TEMPLATE:
      return FileLine;
    case DocumentStatus.DRAFT:
      return EditLine;
    case DocumentStatus.READY_FOR_REVIEW:
      return EditLine;
    case DocumentStatus.PUBLISHED:
      return CheckmarkLine;
    default:
      return undefined;
  }
};

export const getStatusIconColor = (status: DocumentStatus) => {
  if (SWITCHBOARD_DOCUMENT_STATUSES.has(status)) {
    return undefined;
  }

  switch (status) {
    case DocumentStatus.COMPLETED:
      return '#5BA250';
    case DocumentStatus.PUBLISHED:
      return '#5BA250';
    default:
      return undefined;
  }
};

export const getStatusText = (status: DocumentStatus): string => {
  switch (status) {
    case DocumentStatus.IN_REVIEW:
      return t('in_review');
    case DocumentStatus.COMPLETED:
      return t('completed');
    case DocumentStatus.TEMPLATE:
      return t('template');
    case DocumentStatus.DRAFT:
      return t('draft');
    case DocumentStatus.READY_FOR_REVIEW:
      return t('ready_for_review');
    case DocumentStatus.PUBLISHED:
      return t('published');
    case DocumentStatus.ARCHIVED:
      return t('archived');
    case DocumentStatus.CONTEXT:
      return t('switchboard_status_context');
    case DocumentStatus.FRAMING:
      return t('switchboard_status_framing');
    case DocumentStatus.DRAFT_TRACKER:
      return t('switchboard_status_draft_tracker');
    case DocumentStatus.PLANNING_COMPLETE:
      return t('switchboard_status_planning_complete');
    case DocumentStatus.REFRESH:
      return t('switchboard_status_refresh');
    case DocumentStatus.RETROSPECTIVE:
      return t('switchboard_status_retrospective');
    // Legacy Postgres enum label still present on many rows; keep until DB backfill to PLANNING_COMPLETE
    case DocumentStatus.EXECUTION:
      return t('switchboard_status_execution');
    default:
      throw new Error(`Unhandled status: ${status}`);
  }
};
